import {formatDateString, stripTimezone} from '@github-ui/issue-metadata/date-formatting'
import {graphql, useFragment} from 'react-relay'

import type {IssueFieldPills$data, IssueFieldPills$key} from './__generated__/IssueFieldPills.graphql'

type IssueFieldValueNode = NonNullable<NonNullable<IssueFieldPills$data['issueFieldValues']>['nodes']>[number]

export type IssueFieldBadge = {
  fieldName: string
  displayValue: string
  queryValue: string
  color?: string | null
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const issueFieldPillsFragment = graphql`
  fragment IssueFieldPills on Issue @argumentDefinitions(showIssueFieldPills: {type: "Boolean!", defaultValue: false}) {
    issueFieldValues(first: 25) @include(if: $showIssueFieldPills) {
      nodes {
        ... on IssueFieldTextValue {
          __typename
          value
          field {
            ... on IssueFieldText {
              name
            }
          }
        }
        ... on IssueFieldSingleSelectValue {
          __typename
          value
          color
          field {
            ... on IssueFieldSingleSelect {
              name
            }
          }
        }
        ... on IssueFieldDateValue {
          __typename
          value
          field {
            ... on IssueFieldDate {
              name
            }
          }
        }
        ... on IssueFieldNumberValue {
          __typename
          numberValue: value
          field {
            ... on IssueFieldNumber {
              name
            }
          }
        }
      }
    }
  }
`

function getIssueFieldBadge(fieldValue: IssueFieldValueNode): IssueFieldBadge | null {
  if (!fieldValue || fieldValue.__typename === '%other') return null
  const fieldName = fieldValue.field?.name
  if (!fieldName) return null

  switch (fieldValue.__typename) {
    case 'IssueFieldNumberValue': {
      if (fieldValue.numberValue == null) return null
      const displayValue = String(fieldValue.numberValue)
      return {fieldName, displayValue, queryValue: displayValue}
    }
    case 'IssueFieldSingleSelectValue':
      return fieldValue.value
        ? {fieldName, displayValue: fieldValue.value, queryValue: fieldValue.value, color: fieldValue.color}
        : null
    case 'IssueFieldTextValue':
      return fieldValue.value ? {fieldName, displayValue: fieldValue.value, queryValue: fieldValue.value} : null
    case 'IssueFieldDateValue': {
      if (!fieldValue.value) return null
      const date = stripTimezone(fieldValue.value)
      if (!date) return null
      return {fieldName, displayValue: formatDateString(date), queryValue: fieldValue.value.slice(0, 10)}
    }
    default:
      return null
  }
}

// Safe to call with a null key (returns []), so callers can invoke it
// unconditionally and satisfy the rules of hooks.
export function useIssueFieldBadges(dataKey: IssueFieldPills$key | null): IssueFieldBadge[] {
  const data = useFragment(issueFieldPillsFragment, dataKey)
  const nodes = data?.issueFieldValues?.nodes ?? []
  return nodes.flatMap(fieldValue => {
    const badge = getIssueFieldBadge(fieldValue)
    return badge ? [badge] : []
  })
}
